import { Button, Card, CardContent, CardHeader, CloseCircleIcon, ControlError, ControlHelper, Divider, Label, Modal, PlusIcon } from "@forge/common";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./participant-details.scss";
import { COURSE_INSTANCE_CONSTANTS } from "./utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { createCourseInstance, updateCourseInstance, clearCourseInstanceErrors, clearCourseInstanceState } from "./store/start-course-instance.action";
import { AppState } from "../../../../common/store/combinedReducer";
import { CourseOption, GeneralDetails } from "./model";
import { setSelectedCard } from "../view-course-templates/store/view-course-template.action";
import { showToast } from "../store/model/model.action";

interface ParticipantDetailsProps {
  setShowGeneralDetails: (show: boolean) => void;
  updateSteps?: (stepIndex: number, id: string, status: string) => void;
  savedStudents?: string[];
  savedInstructors?: string[];
  saveStudents?: (students: string[]) => void;
  saveInstructors?: (instructors: string[]) => void;
  generalDetailsData?: GeneralDetails;
  instanceId?: string;
  selectedCourse?: any;
  scrollToTop?: () => void;
}

export default function ParticipantDetails({
  setShowGeneralDetails,
  updateSteps,
  savedStudents = [],
  savedInstructors = [],
  saveStudents,
  saveInstructors,
  generalDetailsData = {
    instancename: "",
    description: "",
    batchSize: "",
    startDate: "",
    courseSelect: "",
    courseType: "instructorLed",
    courseDuration: ""
  },
  instanceId,
  selectedCourse,
  scrollToTop

}: ParticipantDetailsProps) {
  const [students, setStudents] = useState<string[]>(savedStudents);
  const [instructors, setInstructors] = useState<string[]>(savedInstructors);
  const [studentInput, setStudentInput] = useState("");
  const [instructorInput, setInstructorInput] = useState("");
  const [studentError, setStudentError] = useState("");
  const [instructorError, setInstructorError] = useState("");
  const [showCancelConfirmation, setShowCancelConfirmation] = useState<boolean>(false);

  const navigate = useNavigate();

  const userDetail: any = useSelector((state: AppState) => {
    return state.reducer.userInfo;
  });

  const isInstructorLed = generalDetailsData!.courseType === "instructorLed";

  useEffect(() => {
    if (scrollToTop) {
      scrollToTop();
    }
  }, [scrollToTop]);

  useEffect(() => {
    if (updateSteps) {
      if (students.length > 0 && (!isInstructorLed || instructors.length > 0)) {
        updateSteps(1, "participants-accepted", "valid");
      } else {
        updateSteps(1, "in-progress", "incomplete");
      }
    }
  }, [students, instructors, isInstructorLed, updateSteps]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = COURSE_INSTANCE_CONSTANTS.EMAIL_REGEX;
    if (email.includes(',')) {
      const emails = email.split(',').map(e => e.trim()).filter(e => e !== '');
      return emails.length > 0 && emails.every(e => emailRegex.test(e));
    }
    return email.length > 0 && emailRegex.test(email);
  };

  const validateEmailLength = (email: string): boolean => {
    return email.length <= 50;
  };

  const emailExistsInArray = (email: string, emailArray: string[]): boolean => {
    return emailArray.some(existingEmail => existingEmail.toLowerCase() === email.toLowerCase());
  };

  const emailExistsInOppositeRole = (email: string, isStudentRole: boolean): boolean => {
    const oppositeRoleArray = isStudentRole ? instructors : students;
    return emailExistsInArray(email, oppositeRoleArray);
  };

  const handleAddStudent = () => {
    if (!studentInput.trim()) {
      setStudentError(COURSE_INSTANCE_CONSTANTS.ENTER_EMAIL);
      return;
    }

    const emailsToAdd = studentInput.split(",").map((email) => email.trim()).filter((email) => email !== "");

    if (emailsToAdd.length === 0) {
      setStudentError(COURSE_INSTANCE_CONSTANTS.ENTER_EMAIL);
      return;
    }

    const lowercaseEmailsToAdd = emailsToAdd.map(email => email.toLowerCase());
    const uniqueEmails = new Set(lowercaseEmailsToAdd);
    if (uniqueEmails.size !== emailsToAdd.length) {
      setStudentError(COURSE_INSTANCE_CONSTANTS.DUPLICATE_EMAIL_FOUND);
      return;
    }

    let hasError = false;
    emailsToAdd.forEach((email) => {
      if (!validateEmail(email)) {
        setStudentError(COURSE_INSTANCE_CONSTANTS.INVALID_EMAIL);
        hasError = true;
      } else if (!validateEmailLength(email)) {
        setStudentError(COURSE_INSTANCE_CONSTANTS.EMAIL_CHARACTER_LIMIT);
        hasError = true;
      } else if (emailExistsInOppositeRole(email, true)) {
        setStudentError(COURSE_INSTANCE_CONSTANTS.STUDENT_INSTRUCTOR_EMAIL_SAME);
        hasError = true;
      }
    });

    if (hasError) return;

    const maxStudentsAllowed = instanceId ? 1 : Number(generalDetailsData!.batchSize);
    if (students.length + emailsToAdd.length > maxStudentsAllowed) {
      setStudentError(`Maximum ${maxStudentsAllowed} student${maxStudentsAllowed === 1 ? '' : 's'} allowed`);
      return;
    }

    const newEmails = emailsToAdd.filter((email) => !emailExistsInArray(email, students));
    if (newEmails.length !== emailsToAdd.length) {
      setStudentError(COURSE_INSTANCE_CONSTANTS.DUPLICATE_EMAIL_FOUND);
      setStudentInput("");
      if (newEmails.length > 0) {
        setStudents([...students, ...newEmails]);
      }
    } else {
      setStudents([...students, ...newEmails]);
      setStudentInput("");
      setStudentError("");
    }
  };

  const handleAddInstructor = () => {
    if (!instructorInput.trim()) {
      setInstructorError(COURSE_INSTANCE_CONSTANTS.ENTER_EMAIL);
      return;
    }

    const emailsToAdd = instructorInput.split(",").map((email) => email.trim()).filter((email) => email !== "");

    if (emailsToAdd.length === 0) {
      setInstructorError(COURSE_INSTANCE_CONSTANTS.ENTER_EMAIL);
      return;
    }

    const lowercaseEmailsToAdd = emailsToAdd.map(email => email.toLowerCase());
    const uniqueEmails = new Set(lowercaseEmailsToAdd);
    if (uniqueEmails.size !== emailsToAdd.length) {
      setInstructorError(COURSE_INSTANCE_CONSTANTS.DUPLICATE_EMAIL_FOUND);
      return;
    }

    let hasError = false;
    emailsToAdd.forEach((email) => {
      if (!validateEmail(email)) {
        setInstructorError(COURSE_INSTANCE_CONSTANTS.INVALID_EMAIL);
        hasError = true;
      } else if (!validateEmailLength(email)) {
        setInstructorError(COURSE_INSTANCE_CONSTANTS.EMAIL_CHARACTER_LIMIT);
        hasError = true;
      } else if (emailExistsInOppositeRole(email, false)) {
        setInstructorError(COURSE_INSTANCE_CONSTANTS.STUDENT_INSTRUCTOR_EMAIL_SAME);
        hasError = true;
      }
    });

    if (hasError) return;

    if (instructors.length + emailsToAdd.length > 4) {
      setInstructorError(COURSE_INSTANCE_CONSTANTS.MAXIMUM_EMAIL_SIZE);
      return;
    }

    const newEmails = emailsToAdd.filter((email) => !emailExistsInArray(email, instructors));
    if (newEmails.length !== emailsToAdd.length) {
      setInstructorError(COURSE_INSTANCE_CONSTANTS.DUPLICATE_EMAIL_FOUND);
      setInstructorInput("");
      if (newEmails.length > 0) {
        setInstructors([...instructors, ...newEmails]);
      }
    } else {
      setInstructors([...instructors, ...newEmails]);
      setInstructorInput("");
      setInstructorError("");
    }
  };

  const dispatch = useDispatch();

  const { courseInstance, updateCourseIns, createCourseInstanceError, updateCourseInstanceError } = useSelector((state: AppState) => {
    return state.courseInstanceReducer
  });

  const [hasSaved, setHasSaved] = useState(false);

  const formatExpiryDate = (dateString?: string): string => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = date.getMilliseconds();

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
  };

  const handleSave = () => {
    dispatch(clearCourseInstanceState());
    setHasSaved(true);
    if (instanceId) {
      dispatch(updateCourseInstance({
        instanceId: instanceId,
        expiryDate: formatExpiryDate(generalDetailsData!.expiryDate),
        userId: userDetail.userId || '',
      }));
    } else {
      dispatch(createCourseInstance({
        templateId: selectedCourse?.vAppTemplate,
        batchName: generalDetailsData!.instancename,
        batchSize: Number(generalDetailsData!.batchSize),
        courseName: generalDetailsData!.courseSelect,
        courseType: generalDetailsData!.courseType === "instructorLed" ? 1 : 2,
        courseInstanceDescription: generalDetailsData!.description,
        courseDefinitionId: selectedCourse?.id,
        instructorEmails: instructors,
        studentEmails: students,
        startDate: formatExpiryDate(generalDetailsData!.startDate),
        createdBy: userDetail.userId || '',
        courseDuration: Number(generalDetailsData!.courseDuration),
      }));
    }
  };

  useEffect(() => {
    if (courseInstance && hasSaved && !instanceId) {
      if (courseInstance.status === 200) {
        dispatch(showToast({
          type: 'success', title: "", content: `${generalDetailsData!.instancename} ${COURSE_INSTANCE_CONSTANTS.SUCCESS_TOAST.CONTENT}`, lifetime:20000
        }))
        dispatch(setSelectedCard(3));
        navigate("/academy/viewCourseTemplates");
      }
    }
  }, [courseInstance, navigate, generalDetailsData.instancename, hasSaved, instanceId, dispatch]);

  useEffect(() => {
    if (updateCourseIns && hasSaved) {
      if (updateCourseIns.status === 200) {
        dispatch(showToast({
          type: 'success', title: "", content: `${generalDetailsData!.instancename} ${COURSE_INSTANCE_CONSTANTS.UPDATE_COURSE_INSTANCE.SUCCESS_TOAST.CONTENT}`, lifetime:20000
        }));
        dispatch(setSelectedCard(3));
        navigate("/academy/viewCourseTemplates");
      }
    }
  }, [updateCourseIns, navigate, generalDetailsData.instancename, hasSaved, dispatch]);

  useEffect(() => {
    if (createCourseInstanceError && hasSaved) {
      dispatch(showToast({
        type: 'error',
        title: COURSE_INSTANCE_CONSTANTS.ERROR_TOAST.ERROR,
        content: createCourseInstanceError.message || COURSE_INSTANCE_CONSTANTS.ERROR_TOAST.CONTENT
      }));
      setHasSaved(false);
      dispatch(clearCourseInstanceErrors());
    }
  }, [createCourseInstanceError, hasSaved, dispatch]);

  useEffect(() => {
    if (updateCourseInstanceError && hasSaved) {
      dispatch(showToast({
        type: 'error',
        title: COURSE_INSTANCE_CONSTANTS.UPDATE_COURSE_INSTANCE.ERROR_TOAST.ERROR,
        content: updateCourseInstanceError.message || COURSE_INSTANCE_CONSTANTS.UPDATE_COURSE_INSTANCE.ERROR_TOAST.CONTENT
      }));
      setHasSaved(false);
      dispatch(clearCourseInstanceErrors());
    }
  }, [updateCourseInstanceError, hasSaved, dispatch]);

  const handleContinueWithoutSaving = () => {
    setShowCancelConfirmation(false);
    dispatch(showToast({
          type: 'warning',
          title: instanceId ? COURSE_INSTANCE_CONSTANTS.CANCEL_WITHOUT_SAVING_TOAST.UPDATION_CANCELLED : COURSE_INSTANCE_CONSTANTS.CANCEL_WITHOUT_SAVING_TOAST.CREATION_CANCELLED,
          subtitle: COURSE_INSTANCE_CONSTANTS.CANCEL_WITHOUT_SAVING_TOAST.NO_DATA_SAVED,
      }))
    dispatch(setSelectedCard(instanceId ? 3 : 1));
    navigate("/academy/viewCourseTemplates");
  }

  return (
    <>
      <Card className="partipant-details-card dark:bg-container-dark !border-none !rounded-none">
        <Label className="label px-6 py-1 !text-base xl-low:!text-[16px] xl-mid:!text-[23px] xl-mid:!mb-4 xl-high:!text-[26px] xl-high:!mb-4">{COURSE_INSTANCE_CONSTANTS.PARTICIPANTS_DETAILS}</Label>
        <div className="px-6 py-1">
          <Divider
            orientation="horizontal"
            subtitle=""
            subtitlePosition="left"
            type="inset"
            variant="full-bleed"
            className="divider"
          />
        </div>
        <CardContent top="condensed">
          <div className="pt-4">
            <div className="grid grid-cols-2 gap-6">
              <div className="grid-cols-1 gap-2 student-details">
                <div>
                  <Label className="label !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]">
                    {COURSE_INSTANCE_CONSTANTS.INVITE_STUDENT}
                  </Label>
                  <div className="flex items-center">
                    <input
                      type="email"
                      multiple
                      placeholder="Email Id"
                      className={`invite-input-field !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px] ${studentError ? 'error-border' : ''}`}
                      value={studentInput}
                      onChange={(e) => {
                        setStudentInput(e.target.value);
                        if (validateEmail(e.target.value) || e.target.value === '') {
                          setStudentError('');
                        }
                      }}
                      id="student-email"
                      data-testid="student-email"
                      disabled={!!instanceId}
                      autoComplete="off"
                    />
                    {!instanceId && (
                      <Button title="add" className="add-button ml-2 flex items-center !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]" type="button" variant="primary" id="add-student-button" data-testid="add-student-button" onClick={handleAddStudent} disabled={!!instanceId}>
                        <span><PlusIcon className="plus-icon" /></span>{" "}{COURSE_INSTANCE_CONSTANTS.ADD}
                      </Button>
                    )}
                  </div>
                  {studentError ? (<ControlError showIcon={false} className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]">{studentError}</ControlError>
                  ) : (
                    <ControlHelper className="info-text !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]">
                      {COURSE_INSTANCE_CONSTANTS.ASSIGN_INSTRUCTOR_MAX} {instanceId ? 1 : Number(generalDetailsData!.batchSize)}
                    </ControlHelper>
                  )}
                </div>
                {students.length > 0 && (
                  <div className="mt-2 invited-list">
                    <ul>
                      {students.map((student, index) => (
                        <li key={index} className="invited-item flex items-center justify-between !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]">
                          <span className="student-name text-ellipsis whitespace-nowrap inline-block" title={student}>{student}</span>
                          <span
                            className={`remove-icon ${instanceId ? 'disabled' : ''}`}
                            onClick={() => {
                              if (!instanceId) {
                                const updatedStudents = [...students];
                                updatedStudents.splice(index, 1);
                                setStudents(updatedStudents);
                                const maxStudentsAllowed = Number(generalDetailsData!.batchSize);
                                if (updatedStudents.length < maxStudentsAllowed) {
                                  setStudentError("");
                                }
                              }
                            }}
                          >
                            <CloseCircleIcon data-testid="CloseCircleIcon" />
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {isInstructorLed && (
                <div className="grid-cols-1 gap-2 student-details">
                  <div>
                    <Label className="label !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]">
                      {COURSE_INSTANCE_CONSTANTS.ASSIGN_INSTRUCTOR}
                    </Label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        placeholder="Email Id"
                        className={`assign-input-field !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px] ${instructorError ? 'error-border' : ''}`}
                        value={instructorInput}
                        onChange={(e) => {
                          setInstructorInput(e.target.value);
                          if (validateEmail(e.target.value) || e.target.value === '') {
                            setInstructorError('');
                          }
                        }}
                        id="instructor-email"
                        data-testid="instructor-email"
                        disabled={!!instanceId}
                        autoComplete="off"
                      />
                      {!instanceId && (
                        <Button title="add" className="add-button ml-2 flex items-center !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]" type="button" id="add-instructor-button" data-testid="add-instructor-button" variant="primary" onClick={handleAddInstructor} disabled={!!instanceId}>
                          <span><PlusIcon className="plus-icon" /></span>{COURSE_INSTANCE_CONSTANTS.ADD}
                        </Button>
                      )}
                    </div>
                    {instructorError ? (
                      <ControlError showIcon={false} className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]">{instructorError}</ControlError>
                    ) : (
                      <ControlHelper className="info-text !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]">
                        {COURSE_INSTANCE_CONSTANTS.ASSIGN_INSTRUCTOR_MAX} 4
                      </ControlHelper>
                    )}
                  </div>
                  {instructors.length > 0 && (
                    <div className="mt-2 invited-list">
                      <ul>
                        {instructors.map((instructor, index) => (
                          <li key={index} className="invited-item flex items-center justify-between !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]">
                            <span className="student-name text-ellipsis whitespace-nowrap inline-block" title={instructor}>{instructor}</span>
                            <span
                              className={`remove-icon ${instanceId ? 'disabled' : ''}`}
                              onClick={() => {
                                if (!instanceId) {
                                  const updatedInstructors = [...instructors];
                                  updatedInstructors.splice(index, 1);
                                  setInstructors(updatedInstructors);
                                  if (updatedInstructors.length < 4) {
                                    setInstructorError("");
                                  }
                                }
                              }}
                            >
                              <CloseCircleIcon data-testid="CloseCircleIcon" />
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
        <div className="flex justify-between w-full mt-2 footer-buttons">
          <div>
            <Button title="previous" type="button" id="previous-step" data-testid="previous-step" onClick={() => {
              if (saveStudents) {
                saveStudents(students);
              }
              if (saveInstructors) {
                saveInstructors(instructors);
              }

              if (updateSteps) {
                if (students.length > 0 && (!isInstructorLed || instructors.length > 0)) {
                  updateSteps(1, "completed", "valid");
                } else {
                  updateSteps(1, "completed", "");
                }
                updateSteps(0, "general-accepted", "valid");
              }

              setShowGeneralDetails(true);
              if (scrollToTop) {
                scrollToTop();
              }
            }}
              variant="quiet-secondary" className="instance-previous-button !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px] xl-high:!h-12 xl-high:!py-3">{COURSE_INSTANCE_CONSTANTS.PREVIOUS_STEP}</Button>
          </div>
          <div className="flex">
            <Button title="cancel" type="button" onClick={() => setShowCancelConfirmation(true)} variant="quiet-secondary" className="mr-4 instance-cancel-button !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px] xl-high:!h-12 xl-high:!py-3" id="instance-creation-cancel" data-testid="instance-creation-cancel">{COURSE_INSTANCE_CONSTANTS.CANCEL}</Button>
            <Button title="next" type="submit" variant="primary" className="!text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px] xl-high:!h-12 xl-high:!py-3" id="instance-creation-save" data-testid="instance-creation-save" onClick={handleSave}>{COURSE_INSTANCE_CONSTANTS.SAVE}</Button>
          </div>
        </div>
      </Card>

      {showCancelConfirmation && (
        <Modal
          backdrop
          className="cancel-confirmation-modal border border-[#E0E0E0] border-[0.2px]"
          footerLeft={
            <div className="flex justify-center">
              <Button
                className="cancel-modal-btns mr-4 w-full"
                id="confirm-cancel"
                data-testid="confirm-cancel"
                onClick={() => setShowCancelConfirmation(false)}
                size="regular"
                variant="secondary"
              >
                {instanceId
                  ? COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_UPDATION.BUTTONS.NO
                  : COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_CREATION.BUTTONS.NO}
              </Button>
              <Button
                className="cancel-modal-btns w-full"
                id="confirm-continue"
                data-testid="confirm-continue"
                onClick={handleContinueWithoutSaving}
                size="regular"
                variant="primary"
              >
                {instanceId
                  ? COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_UPDATION.BUTTONS.YES
                  : COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_CREATION.BUTTONS.YES}

              </Button>
            </div>
          }
          onModalClose={() => setShowCancelConfirmation(false)}
          title={instanceId
            ? COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_UPDATION.TITLE
            : COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_CREATION.TITLE}
        >
          <div className="xl-low:!text-sm xl-mid:!text-base xl-high:!text-lg">
          {instanceId
            ? COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_UPDATION.MESSAGE
            : COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_CREATION.MESSAGE}
          </div>
        </Modal>
      )}
    </>
  );
}
